import { motion } from "framer-motion";
import { Play, Star, Users, Ship, Image as ImageIcon } from "lucide-react";
import { API } from "@/lib/config";

export const monogram = (name) =>
  name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export const photoUrl = (a) => (a.has_photo ? `${API}/applicants/${a.id}/photo` : null);

const ActionBtn = ({ done, label, doneLabel, onClick, testId }) => (
  <button onClick={onClick} disabled={done}
    className={`text-[10px] tracking-[0.2em] uppercase py-2.5 border transition-colors duration-300 ${
      done ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#E8C15A]" : "border-[#D4AF37]/40 text-[#E8C15A] hover:bg-[#D4AF37] hover:text-[#070A0F]"
    }`}
    data-testid={testId}>
    {done ? doneLabel : label}
  </button>
);

export const ApplicantCard = ({ a, i, voted, followed, onVote, onFollow, onOpen, onInquire }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className="bg-[#0D131C] border border-[#D4AF37]/20 flex flex-col hover:border-[#D4AF37]/50 transition-colors duration-300 overflow-hidden"
    data-testid={`applicant-card-${a.id}`}
  >
    {a.kind === "yacht" && (
      <div className="relative h-40 bg-[#070A0F] border-b border-[#D4AF37]/20">
        {photoUrl(a) ? (
          <img src={photoUrl(a)} alt={`${a.name} transom`} className="h-full w-full object-cover" data-testid={`applicant-photo-${a.id}`} />
        ) : (
          <div className="h-full flex items-center justify-center text-[#D4AF37]/40"><ImageIcon size={22} /></div>
        )}
        <span className="absolute top-2 left-2 font-coords text-[9px] tracking-[0.25em] text-[#E8C15A] bg-[#070A0F]/70 border border-[#D4AF37]/30 px-2 py-1">YACHT</span>
      </div>
    )}
    <div className="p-6 flex flex-col flex-1">
      <div className="flex items-center gap-4">
        {a.kind !== "yacht" && (
          <div className="h-14 w-14 rounded-full shrink-0 flex items-center justify-center font-display text-lg text-[#070A0F] bg-gradient-to-b from-[#F3E08A] to-[#997A1E]">
            {monogram(a.name)}
          </div>
        )}
        <div className="min-w-0">
          <h3 className="font-display text-base tracking-[0.06em] text-[#F5F3EF] truncate">{a.name}</h3>
          <p className="font-coords text-[9px] tracking-[0.2em] text-[#E8C15A] uppercase mt-1">
            {[a.role, a.license_level, a.experience].filter(Boolean).join(" · ")}
          </p>
          {a.hometown && <p className="font-coords text-[9px] tracking-[0.2em] text-[#94A3B8] uppercase">{a.hometown}</p>}
        </div>
      </div>
      <p className="text-sm text-[#94A3B8] leading-relaxed mt-4 line-clamp-3">{a.bio}</p>
      {a.highlights?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`applicant-highlights-${a.id}`}>
          {a.highlights.slice(0, 3).map((h) => (
            <span key={h} className="text-[9px] tracking-[0.1em] uppercase text-[#E8C15A]/80 border border-[#D4AF37]/25 px-2 py-0.5">{h}</span>
          ))}
          {a.highlights.length > 3 && <span className="text-[9px] text-[#94A3B8]">+{a.highlights.length - 3}</span>}
        </div>
      )}
      <div className="flex-1" />
      <div className="flex items-center gap-5 mt-5 font-coords text-[10px] tracking-[0.2em] text-[#94A3B8]">
        <span className="flex items-center gap-1.5" data-testid={`applicant-votes-${a.id}`}><Star size={12} className="text-[#E8C15A]" /> {a.votes}</span>
        <span className="flex items-center gap-1.5" data-testid={`applicant-follows-${a.id}`}><Users size={12} className="text-[#E8C15A]" /> {a.follows}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-5">
        <ActionBtn done={voted} label="Vote" doneLabel="Voted" onClick={() => onVote(a)} testId={`vote-btn-${a.id}`} />
        <ActionBtn done={followed} label="Follow" doneLabel="Following" onClick={() => onFollow(a)} testId={`follow-btn-${a.id}`} />
      </div>
      <button onClick={() => onOpen(a)}
        className="mt-2 flex items-center justify-center gap-2 text-[10px] tracking-[0.2em] uppercase py-2.5 bg-[#D4AF37]/10 text-[#E8C15A] hover:bg-[#D4AF37]/20 transition-colors duration-300"
        data-testid={`audition-btn-${a.id}`}>
        <Play size={12} /> {a.kind === "yacht" ? "View Yacht" : "Watch Audition"}
      </button>
      {a.kind === "yacht" && onInquire && (
        <button onClick={() => onInquire(a)}
          className="mt-2 flex items-center justify-center gap-2 text-[10px] tracking-[0.2em] uppercase py-2.5 border border-[#D4AF37]/40 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#070A0F] transition-colors duration-300"
          data-testid={`charter-btn-${a.id}`}>
          <Ship size={12} /> Request to Charter
        </button>
      )}
    </div>
  </motion.article>
);
